import { useLazyQuery, type QueryResult } from '@apollo/client'

import type {
  CommentsInsightsResponse,
  CommentsInsightsInput,
} from '~publish/gql/graphql'
import { graphql } from '~publish/graphql'

export type CommentsInsightsQueryParams = {
  channelId: string
  prompt: string
}

type CommentsInsightsQueryResult = {
  commentsInsights: CommentsInsightsResponse
}

type UseCommentsInsightsReturn = {
  fetchCommentsInsights: (
    params: CommentsInsightsQueryParams,
  ) => Promise<
    QueryResult<CommentsInsightsQueryResult, { input: CommentsInsightsInput }>
  >
}

export const commentsInsightsQuery = graphql(`
  query CommentsInsights($input: CommentsInsightsInput!) {
    commentsInsights(input: $input) {
      insights
    }
  }
`)

/**
 * Hook to fetch comments insights for a given channel and prompt.
 * @returns {UseCommentsInsightsReturn} An object containing the fetchCommentsInsights function
 *
 * @example
 * const { fetchCommentsInsights } = useCommentsInsights()
 * const { data, loading, error } = await fetchCommentsInsights({
 *   channelId: '123',
 *   prompt: 'What are the main themes in our comments?'
 * })
 */
export function useCommentsInsights(): UseCommentsInsightsReturn {
  const [fetchInsights] = useLazyQuery<
    CommentsInsightsQueryResult,
    { input: CommentsInsightsInput }
  >(commentsInsightsQuery, {
    fetchPolicy: 'cache-and-network',
  })

  const fetchCommentsInsights = async (
    params: CommentsInsightsQueryParams,
  ): Promise<
    QueryResult<CommentsInsightsQueryResult, { input: CommentsInsightsInput }>
  > => {
    const { channelId, prompt } = params

    return fetchInsights({
      variables: {
        input: {
          channelId,
          prompt,
        },
      },
    })
  }

  return { fetchCommentsInsights }
}
